"use client";
import React, { useEffect, useRef, useState } from "react";
import {
    discardHTMLTags,
    getBaseImageUrl,
    getMerchantHref,
    getRandomStoreSeoTitle,
    splitHeadingFromDetails,
} from "@/constants/hooks";
import { minimalMerchantData } from "@/services/dataTypes";
import Image from "next/image";
import Link from "next/link";

const INITIAL_COUNT = 7;
const LOAD_MORE_COUNT = 5;
// One store row is ~64px; show exactly 7 rows before scroll kicks in
const CONTAINER_HEIGHT = INITIAL_COUNT * 72;

/* ── Single store row ── */
interface StoreRowProps {
    merchant: minimalMerchantData;
    mer_slug: string;
    mer_slug_type: string;
    domain: string;
}

const StoreRow = ({ merchant, mer_slug, mer_slug_type, domain }: StoreRowProps) => {
    const [heading] = splitHeadingFromDetails(merchant?.details);
    return (
        <Link
            href={getMerchantHref(merchant, mer_slug, mer_slug_type)}
            className="store-horizontal-card d-flex align-items-center justify-content-between text-decoration-none"
        >
            <div className="d-flex align-items-center flex-grow-1 gap-3">
                <div className="store-horizontal-logo-wrapper">
                    <Image
                        src={getBaseImageUrl(domain, merchant?.merchant_logo, "")}
                        alt={`${merchant?.merchant_name} logo`}
                        width={80}
                        height={40}
                        className="store-horizontal-logo"
                        unoptimized
                    />
                </div>
                <div className="store-horizontal-info">
                    <h4 className="store-horizontal-name mb-1">
                        {heading
                            ? discardHTMLTags(heading)
                            : getRandomStoreSeoTitle(merchant?.merchant_name)}
                    </h4>
                </div>
            </div>
            <div className="store-horizontal-action ms-3">
                <span className="btn-store-action">See Offer</span>
            </div>
        </Link>
    );
};

/* ── Main component ── */
interface Props {
    merchants: minimalMerchantData[];
    mer_slug: string;
    mer_slug_type: string;
    domain: string;
}

const StoresInfiniteScroll = ({ merchants, mer_slug, mer_slug_type, domain }: Props) => {
    const [visibleCount, setVisibleCount] = useState(INITIAL_COUNT);
    // ref on the scrollable container — used as IntersectionObserver root
    const scrollBoxRef = useRef<HTMLDivElement | null>(null);
    // ref on the sentinel placed after the last rendered row
    const sentinelRef = useRef<HTMLDivElement | null>(null);

    const hasMore = visibleCount < merchants.length;

    useEffect(() => {
        if (!hasMore) return;
        const scrollBox = scrollBoxRef.current;
        const sentinel = sentinelRef.current;
        if (!scrollBox || !sentinel) return;

        const observer = new IntersectionObserver(
            (entries) => {
                if (entries[0].isIntersecting) {
                    setVisibleCount((prev) =>
                        Math.min(prev + LOAD_MORE_COUNT, merchants.length)
                    );
                }
            },
            {
                root: scrollBox,        // observe inside the scroll box
                rootMargin: "0px",
                threshold: 0.1,
            }
        );

        observer.observe(sentinel);
        return () => observer.unobserve(sentinel);
    }, [hasMore, merchants.length, visibleCount]);

    return (
        /* Scrollable container — fixed height shows exactly 5 rows */
        <div
            ref={scrollBoxRef}
            className="t2-stores-scroll-box mt-3"
            style={{ maxHeight: CONTAINER_HEIGHT, overflowY: "auto" }}
        >
            {merchants.slice(0, visibleCount).map((merchant, i) => (
                <StoreRow
                    key={i}
                    merchant={merchant}
                    mer_slug={mer_slug}
                    mer_slug_type={mer_slug_type}
                    domain={domain}
                />
            ))}

            {/* Sentinel lives inside the scroll box */}
            {hasMore ? (
                <div ref={sentinelRef} className="t2-stores-sentinel">
                    <span className="t2-stores-loading-dot" />
                    <span className="t2-stores-loading-dot" />
                    <span className="t2-stores-loading-dot" />
                </div>
            ) : (
                merchants.length > INITIAL_COUNT && (
                    <p className="t2-stores-end-label">All stores shown</p>
                )
            )}
        </div>
    );
};

export default StoresInfiniteScroll;
